import React, { useState, useEffect } from 'react';
import { benefitsService } from '../services/benefits';
import { storesService } from '../services/stores';
import { Gift, Loader2, Info } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import { Button } from './ui/button';
import { toast } from '../hooks/use-toast';
import { Toaster } from './ui/toaster';

const Benefits = () => {
  const [benefits, setBenefits] = useState([]);
  const [selectedBenefit, setSelectedBenefit] = useState(null);
  const [stores, setStores] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);
        setError(null);
        
        // Cargar beneficios desde el backend (solo activos)
        const benefitsResponse = await benefitsService.getAll();
        if (benefitsResponse.success && benefitsResponse.data) {
          setBenefits(benefitsResponse.data);
        } else {
          throw new Error('No se pudieron cargar los beneficios');
        }

        // Cargar tiendas para información adicional
        const allStores = await storesService.getStores();
        setStores(allStores);
      } catch (err) {
        console.error('Error cargando datos:', err);
        setError(err.message || 'Error al cargar los beneficios');
        toast({
          title: "Error",
          description: "No se pudieron cargar los beneficios",
          variant: "destructive",
        });
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, []);

  // Nota: Se eliminó handleActivate porque ya no es necesario activar manualmente
  // La activación se hace automáticamente al usar el QR en la tienda

  const getStoreInfo = (storeId) => {
    return stores.find(s => s.id === storeId);
  };

  if (loading) {
    return (
      <div className="px-6 py-8">
        <div className="max-w-4xl mx-auto space-y-6">
          <div>
            <h2 className="text-3xl font-bold text-white mb-2">Tus beneficios</h2>
            <p className="text-[#A2A2A2]">Cargando beneficios...</p>
          </div>
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 text-white animate-spin" />
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="px-6 py-8">
        <div className="max-w-4xl mx-auto space-y-6">
          <div>
            <h2 className="text-3xl font-bold text-white mb-2">Tus beneficios</h2>
            <p className="text-[#A2A2A2] text-red-400">{error}</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="px-6 py-8">
        <div className="max-w-4xl mx-auto space-y-6">
          <div>
            <h2 className="text-3xl font-bold text-white mb-2">Tus beneficios</h2>
            <p className="text-[#A2A2A2]">Beneficios disponibles para usar</p>
          </div>

          {/* Benefits List */}
          {benefits.length === 0 ? (
            <div className="bg-white/5 border border-white/10 rounded-2xl p-8 text-center">
              <Gift className="w-16 h-16 text-white/50 mx-auto mb-4" />
              <p className="text-white text-lg mb-2">No hay beneficios disponibles</p>
              <p className="text-[#A2A2A2]">Los beneficios aparecerán aquí cuando estén disponibles</p>
            </div>
          ) : (
            <div className="space-y-3">
              {benefits.map((benefit) => {
                const storeInfo = getStoreInfo(benefit.storeId);
                // Usar logo de la tienda como "arte" del beneficio
                // TODO: Si el modelo Benefit tiene un campo image/banner/artwork, usar ese en lugar de store.logo
                const benefitArt = benefit.image || benefit.banner_url || benefit.artwork || storeInfo?.logo;
                
                return (
                <div
                  key={benefit.id}
                  onClick={() => setSelectedBenefit(benefit)}
                  className="bg-black border border-white/10 rounded-3xl overflow-hidden hover:border-white/30 transition-all cursor-pointer hover:scale-[1.02] active:scale-[0.98]"
                >
                  {/* Arte/Imagen del beneficio en la parte superior */}
                  {benefitArt && (
                    <div className="w-full h-48 bg-white/5 flex items-center justify-center overflow-hidden">
                      <img 
                        src={benefitArt} 
                        alt={benefit.title || benefit.storeName}
                        className="w-full h-full object-cover"
                      />
                    </div>
                  )}
                  
                  <div className="p-6">
                    <div className="flex items-start gap-4">
                      {!benefitArt && (
                        <div className="w-12 h-12 rounded-full bg-white/10 flex items-center justify-center flex-shrink-0">
                          <Gift className="w-6 h-6 text-white" />
                        </div>
                      )}
                      
                      <div className="flex-1">
                        <div className="flex items-start justify-between gap-2 mb-2">
                          <h3 className="text-lg font-semibold text-white">{benefit.storeName}</h3>
                          {benefit.isNew && (
                            <span className="text-xs bg-white/10 text-white px-2 py-1 rounded-full">Nuevo</span>
                          )}
                        </div>
                        <p className="text-white/80 mb-1">{benefit.title}</p>
                        <p className="text-[#A2A2A2] text-sm">{benefit.description}</p>
                        
                        <div className="mt-3 flex items-center gap-2 flex-wrap">
                          {benefit.onePerUser && (
                            <span className="text-xs bg-blue-500/20 text-blue-300 px-3 py-1 rounded-full font-medium border border-blue-500/30">
                              <Info className="w-3 h-3 inline mr-1" />
                              Una vez por cliente
                            </span>
                          )}
                          {!benefit.onePerUser && (
                            <span className="text-xs bg-green-500/20 text-green-300 px-3 py-1 rounded-full font-medium border border-green-500/30">
                              Ilimitado
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* Benefit Detail Dialog */}
      <Dialog open={!!selectedBenefit} onOpenChange={() => setSelectedBenefit(null)}>
        <DialogContent className="bg-black border-white/20 text-white max-w-md">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold">{selectedBenefit?.storeName}</DialogTitle>
          </DialogHeader>
          {selectedBenefit && (
            <div className="space-y-6 pt-4">
              <div className="bg-white/5 rounded-2xl p-4 border border-white/10">
                <p className="text-[#A2A2A2] text-sm mb-2">Beneficio</p>
                <p className="text-white font-medium text-lg">{selectedBenefit.description}</p>
              </div>

              <div>
                <p className="text-[#A2A2A2] text-sm mb-2">Descripción</p>
                <p className="text-white">{getStoreInfo(selectedBenefit.storeId)?.description}</p>
              </div>

              <div>
                <p className="text-[#A2A2A2] text-sm mb-2">Vigencia</p>
                <p className="text-white">{selectedBenefit.validUntil ? `Hasta ${selectedBenefit.validUntil}` : 'Sin fecha de expiración'}</p>
              </div>

              <div>
                <p className="text-[#A2A2A2] text-sm mb-2">Tipo de uso</p>
                <p className="text-white">
                  {selectedBenefit.onePerUser ? (
                    <span className="inline-flex items-center gap-2">
                      <Info className="w-4 h-4 text-blue-300" />
                      Una vez por cliente
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-2">
                      <span className="text-green-300">Ilimitado</span>
                    </span>
                  )}
                </p>
              </div>

              {getStoreInfo(selectedBenefit.storeId)?.conditions && getStoreInfo(selectedBenefit.storeId).conditions.length > 0 && (
                <div>
                  <p className="text-[#A2A2A2] text-sm mb-2">Condiciones</p>
                  <ul className="space-y-1">
                    {getStoreInfo(selectedBenefit.storeId)?.conditions.map((condition, index) => (
                      <li key={index} className="text-white text-sm flex items-start gap-2">
                        <span className="text-[#A2A2A2]">•</span>
                        <span>{condition}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* No es necesario activar manualmente - el beneficio está disponible para usar con el QR */}
              <div className="text-center py-4">
                <p className="text-white font-medium">Beneficio disponible</p>
                <p className="text-[#A2A2A2] text-sm mt-2">Muestra tu QR en la tienda para aplicar el beneficio. Consulta términos y condiciones en tienda. Exclusivo para miembros CCE Puebla</p>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Toaster />
    </>
  );
};

export default Benefits;
